import { AUDIT_PIPELINE_STAGES, isCompletedAuditTask } from '../../utils/auditPipelineUtils';

const NOT_STARTED_STAGE = 'Not started';

/**
 * Groups a flat audit-tasks list into Company -> Project rows, each carrying its current
 * pipeline stage (from buildAuditPipelineStats' stageMap), distinct assignees, and an
 * open/total task count. Powers the "Client Audit Monitoring" section shared by the Sales
 * portfolio view, the TR/Certification broader-visibility view, and the management tier.
 */
export const groupTasksByClient = (tasks = [], stageMap = {}) => {
  const projectsById = new Map();

  (Array.isArray(tasks) ? tasks : []).forEach((task) => {
    const projectId = task?.projectId;
    if (projectId == null || projectId === '') return;
    const key = String(projectId);

    if (!projectsById.has(key)) {
      projectsById.set(key, {
        projectId: key,
        projectCode: task.projectCode || `#${key}`,
        companyName: task.companyName || 'Unknown company',
        standardName: task.standardName || null,
        serviceName: task.serviceName || task.serviceCategory || task.programCategory || null,
        auditCycle: task.auditCycle || null,
        salesOwnerUsername: task.salesOwnerUsername || null,
        stage: stageMap?.[key] || NOT_STARTED_STAGE,
        assigneeSet: new Set(),
        openTaskCount: 0,
        totalTaskCount: 0,
        taskCreatedAt: null,
        lastUpdated: null,
      });
    }

    const project = projectsById.get(key);
    project.totalTaskCount += 1;
    if (!isCompletedAuditTask(task?.status)) project.openTaskCount += 1;
    if (task.assignedUsername) project.assigneeSet.add(task.assignedUsername);
    if (!project.standardName && task.standardName) project.standardName = task.standardName;
    if (!project.serviceName) project.serviceName = task.serviceName || task.serviceCategory || task.programCategory || null;
    if (!project.auditCycle && task.auditCycle) project.auditCycle = task.auditCycle;
    if (!project.salesOwnerUsername && task.salesOwnerUsername) project.salesOwnerUsername = task.salesOwnerUsername;
    const createdAt = task.createdAt ? new Date(task.createdAt).getTime() : null;
    const updatedAt = task.updatedAt ? new Date(task.updatedAt).getTime() : createdAt;
    if (createdAt && (!project.taskCreatedAt || createdAt < project.taskCreatedAt)) project.taskCreatedAt = createdAt;
    if (updatedAt && (!project.lastUpdated || updatedAt > project.lastUpdated)) project.lastUpdated = updatedAt;
  });

  const projectsByCompany = new Map();
  projectsById.forEach((project) => {
    const { assigneeSet, ...rest } = project;
    const companyKey = project.companyName;
    if (!projectsByCompany.has(companyKey)) projectsByCompany.set(companyKey, []);
    projectsByCompany.get(companyKey).push({
      ...rest,
      assignees: Array.from(assigneeSet).sort((a, b) => a.localeCompare(b)),
    });
  });

  return Array.from(projectsByCompany.entries())
    .map(([companyName, projects]) => {
      const sortedProjects = [...projects].sort((a, b) => a.projectCode.localeCompare(b.projectCode));
      return {
        companyName,
        projects: sortedProjects,
        projectCount: sortedProjects.length,
        openTaskCount: sortedProjects.reduce((sum, p) => sum + p.openTaskCount, 0),
      };
    })
    .sort((a, b) => a.companyName.localeCompare(b.companyName, undefined, { sensitivity: 'base' }));
};

/**
 * Stage-order breakdown of every project across the given client groups — the data behind the
 * "Stage Distribution" bar chart above the Client Audit Monitoring table. Stages with zero
 * projects are omitted rather than rendered as an empty segment.
 */
export const resolveStageDistribution = (groups = []) => {
  const allProjects = (Array.isArray(groups) ? groups : []).flatMap((group) => group.projects || []);
  const total = allProjects.length;
  if (total === 0) return [];

  const countsByStage = new Map();
  allProjects.forEach((project) => {
    countsByStage.set(project.stage, (countsByStage.get(project.stage) || 0) + 1);
  });

  return AUDIT_PIPELINE_STAGES
    .map((stage) => ({
      key: stage.key,
      shortLabel: stage.shortLabel,
      color: stage.color,
      tint: stage.tint,
      count: countsByStage.get(stage.key) || 0,
      percentage: Math.round(((countsByStage.get(stage.key) || 0) / total) * 100),
    }))
    .filter((stage) => stage.count > 0);
};

/**
 * Flattens Company -> Project groups into one row per project (each carrying its own
 * companyName) so a table can paginate at the project-row level while still knowing which rows
 * share a company — the UI decides whether to repeat/hide the company label per page.
 */
export const flattenGroupsToRows = (groups = []) => (Array.isArray(groups) ? groups : [])
  .flatMap((group) => (group.projects || []).map((project) => ({ ...project, companyName: group.companyName })));
